/**
 * maintenance_page_v2.js — DAS-109 Phase 07: Maintenance V2 · IS 리프레시 시안 매칭
 *
 * 원본 시안: maintenance.jsx::MaintenanceDesktopV1 / MaintenanceMobileV1
 * UI: 현행 카드 리스트 정돈 — breadcrumb + 상태 필터 pill + 다음 점검 히어로 + 2컬럼 레이아웃
 *
 * 재사용 컴포넌트:
 *   - components/my_page/maintenance_card.js (MaintenanceCard) — CRUD 로직 전체 재사용
 *     onSchedulesChanged prop 포함 (DAS-66 패턴)
 *
 * BE gap G3: 자동화 규칙 — BE 미제공 → placeholder 카드 + TODO(DAS-65-G3)
 * 거래소별 빈도: 기존 리스트에서 FE 집계
 *
 * _isMounted + clearTimeout cleanup
 * resize 리스너
 * silver 토큰 전용. JetBrains Mono 유지. 하드코딩 색상(#hex, rgba) 금지.
 */

import React from 'react';
import dayjs from 'dayjs';
import NavBar from '../common/nav_bar';
import MaintenanceBanner from '../common/maintenance_banner';
import ScrollToTopButton from '../common/scroll_to_top_button';
import MaintenanceCard from '../my_page/maintenance_card';
import { listSchedules } from '../../services/maintenanceApi';
import { getNavCollapsed, setNavCollapsed } from '../../utils/navState';
import { isMobileViewport } from '../../utils/responsive';
import { HexLogo, V2Icon, V2Badge, ExchangeChip, Btn, Card } from '../common/claude_primitives';

/* ── 날짜 헬퍼 ───────────────────────────────────────────── */
const KST_OFFSET = 9 * 60;
const toKSTDayjs = (isoStr) => dayjs(isoStr).utcOffset(KST_OFFSET);

const classifySchedule = (item) => {
    const now = new Date();
    const start = new Date(item.start_at);
    const end = new Date(item.end_at);
    if (end <= now) return 'past';
    if (start <= now) return 'ongoing';
    return 'upcoming';
};

const formatCountdown = (ms) => {
    if (ms <= 0) return '진행 중';
    const totalMin = Math.floor(ms / 60000);
    const days = Math.floor(totalMin / (60 * 24));
    const hours = Math.floor((totalMin % (60 * 24)) / 60);
    const mins = totalMin % 60;
    if (days > 0) return `${days}일 ${hours}시간`;
    if (hours > 0) return `${hours}시간 ${mins}분`;
    return `${mins}분`;
};

/* ── 거래소별 점검 빈도 FE 집계 ──────────────────────────── */
const buildExchangeFrequency = (schedules) => {
    const map = new Map();
    schedules.forEach((s) => {
        const ex = (s.exchange || '').toLowerCase();
        if (!ex) return;
        const start = new Date(s.start_at);
        const end = new Date(s.end_at);
        const hours = (end - start) / 3600000;
        const cur = map.get(ex) || { count: 0, hours: 0 };
        map.set(ex, { count: cur.count + 1, hours: cur.hours + hours });
    });
    return Array.from(map.entries())
        .map(([ex, v]) => ({ ex: ex.toUpperCase(), count: v.count, hours: v.hours.toFixed(1) }))
        .sort((a, b) => b.count - a.count);
};

/* ── 거래소별 빈도 행 ─────────────────────────────────────── */
function FreqRow({ ex, count, hours, maxCount }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 0',
            borderBottom: '1px solid var(--color-surface-border)',
        }}>
            <ExchangeChip name={ex} />
            <div style={{ flex: 1, height: 6, background: 'var(--color-surface-elevated)', borderRadius: 3 }}>
                <div style={{
                    height: '100%',
                    width: `${Math.round((count / maxCount) * 100)}%`,
                    background: 'var(--color-brand)',
                    opacity: 0.65,
                    borderRadius: 3,
                }} />
            </div>
            <span style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11, fontWeight: 600,
                width: 28, textAlign: 'right',
                color: 'var(--color-content)',
            }}>
                {count}
            </span>
            <span style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 10,
                width: 44, textAlign: 'right',
                color: 'var(--color-content-muted)',
            }}>
                {hours}h
            </span>
        </div>
    );
}

/* ── 자동화 규칙 placeholder ──────────────────────────────── */
// TODO(DAS-65-G3): 자동화 규칙 — BE 미제공. 후속 릴리스 예정.
function AutomationRulesPlaceholder() {
    return (
        <Card padding={18}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)' }}>
                    자동화 규칙
                </div>
                <V2Icon name="more" size={16} />
            </div>
            {/* TODO(DAS-65-G3): 자동화 규칙은 BE 미제공 — 후속 릴리스에서 제공됩니다 */}
            <div style={{
                padding: '28px 0',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
                border: '1px dashed var(--color-surface-border)',
                borderRadius: 12,
                background: 'var(--color-surface-elevated)',
            }}>
                <V2Icon name="refresh" size={22} />
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)' }}>
                    자동화 규칙
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-content-muted)', textAlign: 'center', maxWidth: 240 }}>
                    자동화 규칙은 후속 릴리스에서 제공됩니다
                </div>
                <V2Badge tone="neutral">Coming Soon</V2Badge>
            </div>
        </Card>
    );
}

/* ── 상태 필터 pill (시안 V1: 예정 / 진행 중 / 완료) ──────── */
function StatusFilterPills({ scheduledCount, ongoingCount, completedCount, activeStatus, onChangeStatus }) {
    const buttons = [
        { key: 'upcoming', label: '예정', count: scheduledCount },
        { key: 'ongoing',  label: '진행 중', count: ongoingCount },
        { key: 'past',     label: '완료', count: completedCount },
    ];
    return (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {buttons.map((b) => {
                const active = b.key === activeStatus;
                return (
                    <button
                        key={b.key}
                        type="button"
                        onClick={() => onChangeStatus(b.key)}
                        aria-pressed={active}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                            active
                                ? 'bg-brand/15 text-brand-light border-brand/40'
                                : 'bg-surface-card text-content-secondary border-surface-border hover:bg-surface-hover'
                        }`}
                    >
                        {b.label}
                        <span style={{
                            marginLeft: 6,
                            fontFamily: 'JetBrains Mono, monospace',
                            fontSize: 10,
                            color: active ? 'var(--color-brand-light)' : 'var(--color-content-muted)',
                        }}>
                            {b.count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/* ── 데스크톱 V1 IS 리프레시 레이아웃 ─────────────────────── */
function MaintenanceDesktopV1Refreshed({ schedules, loading, activeStatus, onChangeStatus, onSchedulesChanged, onOpenCreateModal, maintenanceCardRef }) {
    const upcomingSchedules = schedules
        .filter((s) => classifySchedule(s) === 'upcoming')
        .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

    const nextSchedule = upcomingSchedules[0] || null;
    const countdownMs = nextSchedule ? new Date(nextSchedule.start_at) - new Date() : 0;

    const freqData = buildExchangeFrequency(schedules);
    const maxCount = freqData.length > 0 ? freqData[0].count : 1;

    const scheduledCount = schedules.filter((s) => classifySchedule(s) === 'upcoming').length;
    const ongoingCount   = schedules.filter((s) => classifySchedule(s) === 'ongoing').length;
    const completedCount = schedules.filter((s) => classifySchedule(s) === 'past').length;

    return (
        <Card padding={0} style={{ overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
                {/* 헤더 — 시안 V1: breadcrumb + 제목 + "새 점검 등록" 버튼 + 필터 pill */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <HexLogo size={18} stroke="var(--color-content-secondary)" accent="var(--color-brand)" />
                        <div>
                            <div style={{
                                fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
                                color: 'var(--color-content-muted)', fontFamily: 'JetBrains Mono, monospace',
                            }}>
                                시스템 운영 / 거래소 점검 일정
                            </div>
                            <h1 style={{
                                fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em',
                                color: 'var(--color-content)', margin: 0,
                            }}>
                                점검 일정 관리
                            </h1>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                        {/* 시안 V1 우측 상단: 새 점검 등록 버튼 */}
                        <Btn variant="primary" size="sm" icon="plus" type="button" onClick={onOpenCreateModal}>
                            새 점검 등록
                        </Btn>
                    </div>
                </div>

                {/* 시안 V1: 상태 필터 pill — 예정 / 진행 중 / 완료 */}
                <StatusFilterPills
                    scheduledCount={scheduledCount}
                    ongoingCount={ongoingCount}
                    completedCount={completedCount}
                    activeStatus={activeStatus}
                    onChangeStatus={onChangeStatus}
                />

                {/* 설명 */}
                <p style={{ fontSize: 12, color: 'var(--color-content-muted)', margin: 0 }}>
                    등록된 점검 시간에는 잔고 캐싱 / 스냅샷 / 청산 알림이 자동 일시 중단됩니다.
                </p>

                {/* 다음 점검 요약 히어로 */}
                {nextSchedule && (
                    <div style={{
                        padding: 18,
                        borderRadius: 14,
                        background: 'var(--color-surface-elevated)',
                        border: '1px solid var(--color-surface-border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: 12,
                    }}>
                        <div>
                            <div style={{
                                fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
                                color: 'var(--color-content-muted)', fontFamily: 'JetBrains Mono, monospace',
                                marginBottom: 4,
                            }}>
                                다음 점검까지
                            </div>
                            <div style={{
                                fontFamily: 'JetBrains Mono, monospace',
                                fontSize: 22, fontWeight: 600,
                                letterSpacing: '-0.02em',
                                color: 'var(--color-content)',
                            }}>
                                {formatCountdown(countdownMs)}
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <ExchangeChip name={nextSchedule.exchange} />
                            <span style={{
                                fontFamily: 'JetBrains Mono, monospace', fontSize: 12,
                                color: 'var(--color-content-secondary)',
                            }}>
                                {toKSTDayjs(nextSchedule.start_at).format('MM-DD HH:mm')} → {toKSTDayjs(nextSchedule.end_at).format('HH:mm')} KST
                            </span>
                        </div>
                    </div>
                )}

                {/* 2컬럼: 점검 CRUD + 사이드 (거래소 빈도 + 자동화 규칙 placeholder) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 16 }}>
                    {/* 점검 CRUD 카드 — MaintenanceCard 전체 재사용 */}
                    <MaintenanceCard ref={maintenanceCardRef} onSchedulesChanged={onSchedulesChanged} />

                    {/* 사이드 */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {/* 거래소별 점검 빈도 */}
                        <Card padding={18}>
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                marginBottom: 14,
                            }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)' }}>
                                    거래소별 점검 빈도
                                </div>
                                <span style={{
                                    fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
                                    color: 'var(--color-content-muted)',
                                }}>
                                    전체 기간
                                </span>
                            </div>
                            {freqData.length === 0 ? (
                                <div style={{ fontSize: 12, color: 'var(--color-content-muted)', padding: '16px 0' }}>
                                    데이터 없음
                                </div>
                            ) : (
                                freqData.map((f) => (
                                    <FreqRow key={f.ex} {...f} maxCount={maxCount} />
                                ))
                            )}
                        </Card>

                        {/* TODO(DAS-65-G3): 자동화 규칙 placeholder */}
                        <AutomationRulesPlaceholder />
                    </div>
                </div>
            </div>
        </Card>
    );
}

/* ── 모바일 V1 IS 리프레시 레이아웃 ──────────────────────── */
function MaintenanceMobileV1Refreshed({ schedules, loading, activeStatus, onChangeStatus, onSchedulesChanged, onOpenCreateModal, maintenanceCardRef }) {
    const upcomingSchedules = schedules
        .filter((s) => classifySchedule(s) === 'upcoming')
        .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

    const nextSchedule = upcomingSchedules[0] || null;
    const countdownMs = nextSchedule ? new Date(nextSchedule.start_at) - new Date() : 0;

    const scheduledCount = schedules.filter((s) => classifySchedule(s) === 'upcoming').length;
    const ongoingCount   = schedules.filter((s) => classifySchedule(s) === 'ongoing').length;
    const completedCount = schedules.filter((s) => classifySchedule(s) === 'past').length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* 헤더 카드 — 시안 V1 모바일: "새 점검 등록" 버튼 + 상태 필터 pill */}
            <Card padding={16}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <HexLogo size={16} stroke="var(--color-content-secondary)" accent="var(--color-brand)" />
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-content)' }}>
                            점검 일정 관리
                        </div>
                    </div>
                    {/* 시안 V1 모바일 상단: 새 점검 등록 버튼 */}
                    <Btn variant="primary" size="sm" icon="plus" type="button" onClick={onOpenCreateModal}>
                        새 점검 등록
                    </Btn>
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-content-muted)', marginBottom: 10 }}>
                    시스템 운영 / 거래소 점검 일정
                </div>
                {/* 시안 V1 모바일 — 상태 필터 pill 가로 스크롤 */}
                <div style={{ overflowX: 'auto' }}>
                    <StatusFilterPills
                        scheduledCount={scheduledCount}
                        ongoingCount={ongoingCount}
                        completedCount={completedCount}
                        activeStatus={activeStatus}
                        onChangeStatus={onChangeStatus}
                    />
                </div>
            </Card>

            {/* 다음 점검 히어로 */}
            <Card padding={16}>
                <div style={{
                    fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: 'var(--color-content-muted)', fontFamily: 'JetBrains Mono, monospace',
                    marginBottom: 6,
                }}>
                    다음 점검까지
                </div>
                <div style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 24, fontWeight: 600,
                    letterSpacing: '-0.02em',
                    color: 'var(--color-content)',
                }}>
                    {nextSchedule ? formatCountdown(countdownMs) : '예정 없음'}
                </div>
                {nextSchedule && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ExchangeChip name={nextSchedule.exchange} />
                        <span style={{
                            fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
                            color: 'var(--color-content-secondary)',
                        }}>
                            {toKSTDayjs(nextSchedule.start_at).format('MM-DD HH:mm')} KST
                        </span>
                    </div>
                )}
            </Card>

            {/* 점검 CRUD 카드 */}
            <Card padding={14}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-content)', marginBottom: 10 }}>
                    점검 일정 관리
                </div>
                <p style={{ fontSize: 11, color: 'var(--color-content-muted)', marginBottom: 12 }}>
                    등록된 시간 동안 모든 모니터링이 자동 일시 중단됩니다.
                </p>
                <MaintenanceCard ref={maintenanceCardRef} onSchedulesChanged={onSchedulesChanged} />
            </Card>

            {/* TODO(DAS-65-G3): 자동화 규칙 placeholder */}
            <AutomationRulesPlaceholder />
        </div>
    );
}

/* ── 메인 클래스 컴포넌트 ─────────────────────────────────── */
class MaintenancePageV2 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            schedules: [],
            loading: true,
            isMobile: isMobileViewport(),
            // 시안 V1 상태 필터 pill — 기본은 "예정"
            activeStatus: 'upcoming',
        };
        this.pollTimer = null;
        this._isMounted = false;
        this._onResize = this._onResize.bind(this);
        this.maintenanceCardRef = React.createRef();
    }

    onChangeStatus = (key) => {
        this.setState({ activeStatus: key });
    };

    _onResize() {
        const m = isMobileViewport();
        if (m !== this.state.isMobile) this.setState({ isMobile: m });
    }

    componentDidMount() {
        this._isMounted = true;
        this.loadSchedules();
        window.addEventListener('resize', this._onResize);
    }

    componentWillUnmount() {
        this._isMounted = false;
        clearTimeout(this.pollTimer);
        window.removeEventListener('resize', this._onResize);
    }

    loadSchedules = async () => {
        this.setState({ loading: true });
        try {
            const res = await listSchedules();
            if (!this._isMounted) return;
            this.setState({
                schedules: Array.isArray(res.data) ? res.data : [],
                loading: false,
            });
        } catch (_e) {
            if (!this._isMounted) return;
            this.setState({ loading: false });
        }
        // 5분 주기 폴링 (마운트 상태일 때만 예약)
        if (this._isMounted) {
            this.pollTimer = setTimeout(this.loadSchedules, 5 * 60 * 1000);
        }
    };

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    // MaintenanceCard CRUD 후 캘린더 즉시 동기화 (DAS-66 패턴)
    onSchedulesChanged = () => {
        this.loadSchedules();
    };

    render() {
        const { collapsed, schedules, loading, isMobile, activeStatus } = this.state;

        const onOpenCreateModal = () => {
            this.maintenanceCardRef.current?.onOpenCreateModal();
        };

        const layoutProps = {
            schedules,
            loading,
            activeStatus,
            onChangeStatus: this.onChangeStatus,
            onSchedulesChanged: this.onSchedulesChanged,
            onOpenCreateModal,
            maintenanceCardRef: this.maintenanceCardRef,
        };

        return (
            <div className="min-h-screen w-full bg-surface text-content sm:overflow-x-auto">
                <NavBar collapsed={collapsed} onCollapse={this.onCollapse} />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${
                        collapsed ? 'sm:pl-[56px]' : 'sm:pl-[220px]'
                    }`}
                    style={{ marginTop: 'var(--maintenance-banner-h, 0px)' }}
                >
                    <MaintenanceBanner />
                    <div className="mx-3 my-3 min-w-0 sm:mx-6 sm:my-5">
                        {isMobile
                            ? <MaintenanceMobileV1Refreshed {...layoutProps} />
                            : <MaintenanceDesktopV1Refreshed {...layoutProps} />
                        }
                    </div>
                </main>
                <ScrollToTopButton />
            </div>
        );
    }
}

export default MaintenancePageV2;
